import { Component, Input, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { IonicModule } from '@ionic/angular';
import { TranslateModule } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-birthdate-field',
  templateUrl: './birthdate-field.component.html',
  styleUrls: ['./birthdate-field.component.scss'],
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, IonicModule, TranslateModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => BirthdateFieldComponent),
      multi: true
    }
  ]
})
export class BirthdateFieldComponent implements ControlValueAccessor {
  @Input() submitted: boolean = false;
  @Input() role: string = '';
  @Input() minAge: number = 18;
  @Input() maxAge: number = 100;
  @Input() required: boolean = false;
  @Input() set minAgeInput(val: number | undefined) {
    this.minAge = val !== undefined ? val : 18;
    this.updateValidators();
  }
  @Input() set maxAgeInput(val: number | undefined) {
    this.maxAge = val !== undefined ? val : 100;
    this.updateValidators();
  }

  birthdateForm: FormGroup;

  private onChange = (value: any) => {};
  private onTouched = () => {};

  constructor(private fb: FormBuilder) {
    this.birthdateForm = this.fb.group({
      birth_date: ['', this.getValidators()]
    });

    this.birthdateForm.valueChanges.subscribe(value => {
      this.onChange(value);
    });
  }

  private getValidators() {
    const validators = [];
    if (this.required) {
      validators.push(Validators.required);
    }
    validators.push(this.ageRangeValidator());
    return validators;
  }

  private updateValidators() {
    const birthdateControl = this.birthdateForm.get('birth_date');
    if (birthdateControl) {
      birthdateControl.setValidators(this.getValidators());
      birthdateControl.updateValueAndValidity();
    }
  }

  private ageRangeValidator(): (control: any) => { [key: string]: any } | null {
    return (control: any) => {
      const value = control.value;
      if (!value) return null;
      const birthDate = new Date(value);
      const today = new Date();
      // Interdire la date du jour ou future
      if (birthDate >= today) {
        return { invalidDate: true };
      }
      let age = today.getFullYear() - birthDate.getFullYear();
      const m = today.getMonth() - birthDate.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      if (this.minAge !== undefined && age < this.minAge) {
        return { minAge: { requiredAge: this.minAge, actualAge: age } };
      }
      if (this.maxAge !== undefined && age > this.maxAge) {
        return { maxAge: { requiredAge: this.maxAge, actualAge: age } };
      }
      return null;
    };
  }

  // Update validators when role changes
  updateRole(role: string) {
    this.role = role;
    this.updateValidators();
  }
  // Update validators when required changes
  ngOnChanges() {
    this.updateValidators();
  }

  writeValue(value: any): void {
    if (value) {
      this.birthdateForm.patchValue(value, { emitEvent: false });
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.birthdateForm.disable();
    } else {
      this.birthdateForm.enable();
    }
  }

  get birthdateControl() {
    return this.birthdateForm.get('birth_date');
  }
}
